import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/services/api";
import Loader from "@/components/loader";
import { FiArrowLeft, FiCheckCircle, FiClock, FiMessageSquare, FiTarget, FiAlertCircle, FiZap, FiStar, FiMessageCircle } from "react-icons/fi";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import Markdown from "react-markdown";

// Self-contained Coach Insights Component
function CoachInsights({ application }) {
  // Don't render if no insights are available (using flattened fields)
  if (application?.coaching_match_score === undefined) return null;

  const priorityConfig = {
    high: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-500", label: "Important" },
    medium: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-500", label: "Conseil" },
    low: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-500", label: "Astuce" },
  };

  const scoreColor =
    application.coaching_match_score >= 70 ? "text-emerald-600 border-emerald-200" : application.coaching_match_score >= 50 ? "text-amber-600 border-amber-200" : "text-red-600 border-red-200";

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-50 to-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
            <FiZap className="w-5 h-5" />
          </div>
          <h2 className="font-semibold text-gray-900">Conseils du Coach</h2>
        </div>
        <div className={`w-12 h-12 rounded-full border-4 flex items-center justify-center bg-white font-bold ${scoreColor}`}>{application.coaching_match_score}%</div>
      </div>

      <div className="p-6 space-y-5">
        {/* Summary */}
        {application.coaching_summary && <p className="text-gray-700 font-medium">{application.coaching_summary}</p>}

        {/* Strengths */}
        {application.coaching_strengths?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <FiStar className="text-emerald-500" /> Vos points forts
            </h4>
            <ul className="space-y-1.5">
              {application.coaching_strengths.map((s, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tips */}
        {application.coaching_tips?.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Conseils personnalisés</h4>
            {application.coaching_tips.map((tip, i) => {
              const config = priorityConfig[tip.priority] || priorityConfig.low;
              return (
                <div key={i} className={`${config.bg} ${config.border} border rounded-lg p-3`}>
                  <div className="flex items-start gap-2">
                    <FiAlertCircle className={`${config.icon} mt-0.5 shrink-0`} />
                    <div>
                      <h5 className="text-sm font-medium text-gray-900">{tip.title}</h5>
                      <p className="text-sm text-gray-600 mt-0.5">{tip.message}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Interview Questions */}
        {application.coaching_interview_questions?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <FiMessageCircle className="text-violet-500" /> Questions d'entretien possibles
            </h4>
            <ul className="space-y-2">
              {application.coaching_interview_questions.map((q, i) => (
                <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const translations = {
  en: {
    back: "Back to applications",
    status: {
      APPLIED: "Applied",
      SCREENING: "Screening",
      INTERVIEW: "Interview",
      OFFER: "Offer",
      HIRED: "Hired",
      REFUSED: "Declined",
    },
    coach: {
      title: "Coach Insights",
      match_score: "Match Score",
      strong_match: "You are a strong match!",
      tips: "To improve your profile, consider highlighting your leadership experiences.",
    },
    tabs: {
      tracker: "Process",
      job: "Job Details",
      me: "My Application",
    },
    tracker: {
      applied: "Application received",
      screening: "Application under review",
      interview: "Interview process",
      offer: "Offer received",
      hired: "Hired",
    },
    job: {
      description: "Job Description",
      location: "Location",
      contract: "Contract Type",
    },
  },
  fr: {
    back: "Retour aux candidatures",
    status: {
      APPLIED: "Candidaté",
      SCREENING: "En examen",
      INTERVIEW: "Entretien",
      OFFER: "Offre",
      HIRED: "Recruté",
      REFUSED: "Refusé",
    },
    coach: {
      title: "Conseils du Coach",
      match_score: "Score de match",
      strong_match: "Vous correspondez bien au profil !",
      tips: "Pour améliorer votre profil, pensez à mettre en avant vos expériences de leadership.",
    },
    tabs: {
      tracker: "Suivi",
      job: "Offre",
      me: "Ma candidature",
    },
    tracker: {
      applied: "Candidature reçue",
      screening: "Candidature en cours d'examen",
      interview: "Processus d'entretien",
      offer: "Offre reçue",
      hired: "Recruté",
    },
    job: {
      description: "Description du poste",
      location: "Lieu",
      contract: "Type de contrat",
    },
  },
};

export default function ApplicationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useComponentTranslation(translations);
  const [application, setApplication] = useState(null);
  const [job, setJob] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingChat, setStartingChat] = useState(false);

  const handleStartChat = async () => {
    if (!application?._id) return;
    try {
      setStartingChat(true);
      const { ok, data } = await api.post("/conversation", { application_id: application._id });
      if (!ok) throw new Error("Could not start conversation");
      navigate(`/candidate/chat/${data._id}`);
    } catch (e) {
      console.error(e);
      toast.error("Error starting conversation");
    } finally {
      setStartingChat(false);
    }
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        // Fetch Application
        const { ok, data: appData } = await api.get(`/application/${id}`);
        if (!ok) throw new Error("Could not load application");
        setApplication(appData);

        const { ok: jobOk, data: jobData } = await api.get(`/job/${appData.job_id}`);
        if (jobOk) setJob(jobData);

        // Fetch Stages
        const { ok: stagesOk, data: stagesData } = await api.post("/stage/search", { job_id: appData.job_id });
        if (stagesOk) {
          setStages(stagesData.filter((stage) => stage.key !== "REFUSED"));
        }
      } catch (e) {
        console.error(e);
        toast.error("Error loading details");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDetails();
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center mt-20">
        <Loader />
      </div>
    );
  if (!application) return <div>Application not found</div>;

  // Determine current stage index based on application status or stage ID match
  const currentStageIndex = stages.findIndex((s) => s.key === application.status) !== -1 ? stages.findIndex((s) => s.key === application.status) : 0;

  return (
    <div className="max-w-5xl mx-auto pb-12">
      {/* Header Navigation */}
      <Link to="/candidate/application" className="inline-flex items-center text-sm text-gray-500 hover:text-primary mb-6 transition-colors">
        <FiArrowLeft className="mr-2" /> {t("back")}
      </Link>

      {/* Main Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{job?.title || application.job_title}</h1>
            <p className="text-gray-600 font-medium">{application.organization_name}</p>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-bold ${application.status === "REFUSED" ? "bg-red-100 text-red-700" : "bg-primary/10 text-primary"}`}>
            {t(`status.${application.status}`) || application.status}
          </span>
        </div>

        {/* Process Stepper */}
        {stages.length > 0 && (
          <div className="mt-8 relative hidden md:block">
            {/* Connecting Line */}
            <div className="absolute top-4 left-0 w-full h-1 bg-gray-100 rounded z-0" />
            <div
              className="absolute top-4 left-0 h-1 bg-primary transition-all duration-500 rounded z-0"
              style={{ width: `${(currentStageIndex / (stages.length - 1)) * 100}%` }}
            />

            <div className="relative z-10 flex justify-between">
              {stages.map((stage, index) => {
                const isCompleted = index <= currentStageIndex;
                const isCurrent = index === currentStageIndex;

                return (
                  <div key={stage._id} className="flex flex-col items-center flex-1">
                    <div
                      className={`
                      w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-300 z-10 bg-white
                      ${isCompleted ? "bg-primary border-primary text-white" : "border-gray-300 text-gray-300"}
                    `}
                    >
                      {isCompleted ? <FiCheckCircle /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                    </div>
                    <span className={`mt-2 text-xs font-medium uppercase tracking-wider text-center ${isCurrent ? "text-primary" : "text-gray-500"}`}>{stage.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Action Center - Optional (mockup for now) */}
          {application.status === "INTERVIEW" && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-2">
                <FiClock /> Interview Pending
              </h3>
              <p className="text-sm text-blue-800 mb-4">The recruiter has moved your application to the interview stage. Expect to hear from them soon to schedule a time.</p>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">View Availability</button>
            </div>
          )}

          {/* Coach / Insights */}
          <CoachInsights application={application} />

          {/* Job Description Preview */}
          {job && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t("job.description")}</h3>
              <div className="prose prose-sm max-w-none text-gray-600">
                <Markdown>{job.description}</Markdown>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">{t("job.location")}</h3>
            {job?.address && (
              <div className="flex items-start gap-3 text-gray-600 mb-4">
                <FiTarget className="mt-1" />
                <span className="text-sm">{job.address}</span>
              </div>
            )}

            <h3 className="font-semibold text-gray-900 mb-4 mt-6">{t("job.contract")}</h3>
            <div className="flex flex-wrap gap-2">
              {job?.contract && <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{job.contract}</span>}
              {job?.workplace_type && <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">{job.workplace_type}</span>}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Recruiter</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-bold">{application.organization_name?.charAt(0)}</div>
              <div>
                <p className="text-sm font-medium text-gray-900">{application.organization_name}</p>
                <button onClick={handleStartChat} disabled={startingChat} className="text-xs text-primary hover:underline mt-0.5 flex items-center gap-1 disabled:opacity-50">
                  <FiMessageSquare className="w-3 h-3" /> {startingChat ? "..." : "Send Message"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
